import { GlobalErrorFallback } from '@/components/system/global-error-fallback'
import { NotFoundPage } from '@/components/system/not-found-page'
import { Skeleton } from '@/components/ui/skeleton'
import { TooltipProvider } from '@/components/ui/tooltip'
import { type QueryClient, QueryErrorResetBoundary } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
  Outlet,
  ScrollRestoration,
  createRootRouteWithContext,
  useRouter,
} from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import { Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: () => <NotFoundPage />,
})

function RootComponent() {
  const router = useRouter()
  // "Спробувати знову" must hit two layers:
  // 1. QueryErrorResetBoundary.reset() — discards cached errors from
  //    useSuspenseQuery so the next render refetches instead of rethrowing.
  // 2. router.invalidate() — re-runs every matched route's loader and
  //    beforeLoad guard (incl. requireAuth's GET /auth/me). Without this
  //    a 502 captured during beforeLoad stays cached at the router level,
  //    so resetting queries alone leaves the same matched-route error.
  return (
    <TooltipProvider delayDuration={500}>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            onReset={() => {
              reset()
              void router.invalidate()
            }}
            FallbackComponent={({ error, resetErrorBoundary }) => (
              <GlobalErrorFallback error={error} reset={resetErrorBoundary} />
            )}
          >
            <Suspense fallback={<RootSkeleton />}>
              <Outlet />
            </Suspense>
            <ScrollRestoration />
            {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
            {import.meta.env.DEV && <ReactQueryDevtools position="bottom" />}
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </TooltipProvider>
  )
}

function RootSkeleton() {
  return (
    <div className="flex h-screen items-center justify-center p-6">
      <Skeleton className="h-12 w-32" />
    </div>
  )
}
